import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Home } from 'lucide-react';
import { AuthContext } from '../App';

const UNSAVED_MSG = 'You have unsaved changes. Leave without saving?';

// Role-based home destination.
const homeFor = (user) => {
  if (!user) return '/auth';
  if (user.role === 'admin' || user.base_role === 'admin') return '/admin';
  if (user.role === 'care_provider') return '/provider-dashboard';
  if (user.role === 'care_seeker') return '/seeker-dashboard';
  return '/';
};

/**
 * Slim, sticky, brand-consistent top app bar with Back (left) + Home (right).
 * Reusable across Care Seeker, Responder, Therapist and Admin journeys.
 *
 * Props:
 *  - title?: string                 optional centered page title
 *  - showBack?: boolean (default true)
 *  - showHome?: boolean (default true)
 *  - hasUnsavedChanges?: boolean    prompts before navigating away
 *  - onBack?: () => void            override default back behaviour
 *  - rightSlot?: node               extra actions grouped with Home
 *  - compact?: boolean              Back-only slim bar for immersive screens
 *  - dark?: boolean                 translucent dark styling for call screens
 */
export const AppHeader = ({
  title,
  showBack = true,
  showHome = true,
  hasUnsavedChanges = false,
  onBack,
  rightSlot,
  compact = false,
  dark = false,
}) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};

  const confirmLeave = () => !hasUnsavedChanges || window.confirm(UNSAVED_MSG);

  const handleBack = () => {
    if (!confirmLeave()) return;
    if (onBack) return onBack();
    // Avoid leaving the app / history loops: fall back to role home.
    if (window.history.length > 2) navigate(-1);
    else navigate(homeFor(user));
  };

  const handleHome = () => {
    if (!confirmLeave()) return;
    navigate(homeFor(user));
  };

  const barClass = dark
    ? 'bg-black/40 backdrop-blur-sm text-white'
    : 'bg-[#7D202B] text-[#FFFDFC] shadow-sm';
  const rowHeight = compact ? 'h-11' : 'h-14';
  const btnClass = dark ? 'hover:bg-white/20' : 'hover:bg-white/10';

  return (
    <header
      className={`sticky top-0 z-40 w-full ${barClass}`}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      data-testid="app-header"
    >
      <div className={`flex items-center justify-between ${rowHeight} px-2 sm:px-4 gap-2`}>
        <div className="flex items-center min-w-[44px]">
          {showBack && (
            <button
              type="button"
              onClick={handleBack}
              aria-label="Back"
              title="Back"
              data-testid="app-header-back"
              className={`flex items-center justify-center min-w-[44px] min-h-[44px] rounded-full ${btnClass} transition-colors`}
            >
              <ChevronLeft size={22} />
              {!compact && <span className="hidden md:inline text-sm ml-1">Back</span>}
            </button>
          )}
        </div>

        {title && (
          <h1 className="text-base sm:text-lg font-heading font-semibold truncate text-center flex-1">
            {title}
          </h1>
        )}

        <div className="flex items-center justify-end gap-1 min-w-[44px]">
          {rightSlot}
          {showHome && !compact && (
            <button
              type="button"
              onClick={handleHome}
              aria-label="Home"
              title="Home"
              data-testid="app-header-home"
              className={`flex items-center justify-center min-w-[44px] min-h-[44px] rounded-full ${btnClass} transition-colors`}
            >
              <Home size={20} />
              <span className="hidden md:inline text-sm ml-1">Home</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
